import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { 
  Trophy, 
  Star, 
  Medal, 
  Crown, 
  Zap, 
  Target,
  Flame,
  BookOpen,
  Brain,
  Award,
  Gift,
  Sparkles
} from "lucide-react";

function GamificationSystem() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAchievement, setShowAchievement] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          setUserData(snap.data());
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Calculate user stats
  const completedLessons = userData?.completedLessons?.length || 0;
  const vocabWords = Object.keys(userData?.vocabularyProgress || {}).length;
  const studyStreak = userData?.studyStreak || 0;
  const totalStudyTime = userData?.totalStudyTime || 0;
  const quizScores = userData?.quizScores || [];
  const averageScore = quizScores.length > 0 
    ? Math.round(quizScores.reduce((sum, score) => sum + score.score, 0) / quizScores.length)
    : 0;

  // Calculate total points
  const calculatePoints = () => {
    let points = 0;
    points += completedLessons * 100; // 100 points per lesson
    points += vocabWords * 10; // 10 points per vocabulary word
    points += studyStreak * 50; // 50 points per day streak
    points += Math.floor(totalStudyTime / 10); // 1 point per 10 minutes
    points += averageScore; // Points based on average quiz score
    return points;
  };

  const totalPoints = calculatePoints();

  // Calculate level based on points
  const calculateLevel = (points) => {
    if (points < 1000) return { level: 1, name: "Beginner", color: "from-gray-400 to-gray-600" };
    if (points < 2500) return { level: 2, name: "Novice", color: "from-green-400 to-green-600" };
    if (points < 5000) return { level: 3, name: "Intermediate", color: "from-blue-400 to-blue-600" };
    if (points < 10000) return { level: 4, name: "Advanced", color: "from-purple-400 to-purple-600" };
    if (points < 20000) return { level: 5, name: "Expert", color: "from-orange-400 to-orange-600" };
    return { level: 6, name: "Master", color: "from-red-400 to-red-600" };
  };

  const userLevel = calculateLevel(totalPoints);
  const levelThresholds = [0, 1000, 2500, 5000, 10000, 20000];
  const currentThreshold = levelThresholds[userLevel.level - 1] || 0;
  const nextThreshold = levelThresholds[userLevel.level] || totalPoints;
  
  const progressToNextLevel = userLevel.level === 6 ? 100 : 
    Math.round(((totalPoints - currentThreshold) / (nextThreshold - currentThreshold)) * 100);

  // Define achievements
  const achievements = [
    {
      id: "first_lesson",
      title: "First Steps",
      description: "Complete your first lesson",
      icon: BookOpen,
      points: 100,
      unlocked: completedLessons > 0,
      color: "from-blue-400 to-blue-600"
    },
    {
      id: "vocab_10",
      title: "Word Collector",
      description: "Learn 10 vocabulary words",
      icon: Brain,
      points: 200,
      unlocked: vocabWords >= 10,
      color: "from-green-400 to-green-600"
    },
    {
      id: "streak_7",
      title: "Week Warrior",
      description: "Study for 7 days straight",
      icon: Flame,
      points: 500,
      unlocked: studyStreak >= 7,
      color: "from-orange-400 to-orange-600"
    },
    {
      id: "vocab_50",
      title: "Vocabulary Builder",
      description: "Learn 50 vocabulary words",
      icon: Brain,
      points: 500,
      unlocked: vocabWords >= 50,
      color: "from-purple-400 to-purple-600"
    },
    {
      id: "streak_30",
      title: "Month Master",
      description: "Study for 30 days straight",
      icon: Crown,
      points: 1500,
      unlocked: studyStreak >= 30,
      color: "from-red-400 to-red-600"
    },
    {
      id: "perfect_score",
      title: "Perfectionist",
      description: "Get 100% on a quiz",
      icon: Star,
      points: 300,
      unlocked: averageScore >= 100,
      color: "from-yellow-400 to-yellow-600"
    },
    {
      id: "vocab_100",
      title: "Word Master",
      description: "Learn 100 vocabulary words",
      icon: Trophy,
      points: 1000,
      unlocked: vocabWords >= 100,
      color: "from-indigo-400 to-indigo-600"
    },
    {
      id: "all_lessons",
      title: "Korean Scholar",
      description: "Complete all lessons",
      icon: Medal,
      points: 2600,
      unlocked: completedLessons >= 26,
      color: "from-pink-400 to-pink-600"
    }
  ];

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* User Level & Points */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className={`w-24 h-24 rounded-full bg-gradient-to-r ${userLevel.color} flex items-center justify-center shadow-2xl`}>
              <Crown className="w-12 h-12 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 bg-yellow-400 text-black text-sm font-bold px-3 py-1 rounded-full animate-bounce">
              {userLevel.level}
            </div>
          </div>
          
          <h2 className="text-3xl font-black text-gray-900 mb-2">{userLevel.name}</h2>
          <div className="text-2xl font-bold text-red-600 mb-4">
            {totalPoints.toLocaleString()} Points
          </div>
          
          {userLevel.level < 6 && (
            <div className="max-w-md mx-auto">
              <div className="flex justify-between text-sm font-semibold text-gray-700 mb-2">
                <span>Progress to Next Level</span>
                <span>{Math.round(progressToNextLevel)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full bg-gradient-to-r ${userLevel.color} transition-all duration-500`}
                  style={{ width: `${progressToNextLevel}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600 mt-2">
                {nextThreshold - totalPoints} points to next level
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Daily Streak */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-amber-200">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <Flame className="w-8 h-8 text-orange-600 mr-3" />
            <h3 className="text-2xl font-black text-gray-900">Study Streak</h3>
          </div>
          
          <div className="text-6xl font-black text-orange-600 mb-2">{studyStreak}</div>
          <div className="text-lg text-gray-700 mb-4">Days in a row</div>
          
          <div className="flex justify-center space-x-2">
            {Array.from({ length: 7 }, (_, i) => (
              <div 
                key={i} 
                className={`w-8 h-8 rounded-full border-2 ${
                  i < studyStreak % 7 
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 border-orange-500' 
                    : 'bg-gray-200 border-gray-300'
                }`}
              />
            ))}
          </div>
          
          <div className="text-sm text-gray-600 mt-4">
            Keep studying daily to maintain your streak!
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-2xl p-8 border-4 border-purple-200">
        <div className="flex items-center justify-center mb-8">
          <Trophy className="w-8 h-8 text-purple-600 mr-3" />
          <h3 className="text-2xl font-black text-gray-900">Achievements</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Unlocked Achievements */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Sparkles className="w-5 h-5 text-yellow-500 mr-2" />
              Unlocked ({unlockedAchievements.length})
            </h4>
            <div className="space-y-3">
              {unlockedAchievements.map((achievement) => (
                <div key={achievement.id} className="flex items-center p-4 bg-white rounded-xl shadow-lg border-2 border-green-200">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${achievement.color} flex items-center justify-center mr-4`}>
                    <achievement.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900">{achievement.title}</div>
                    <div className="text-sm text-gray-600">{achievement.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-green-600">+{achievement.points}</div>
                    <div className="text-xs text-gray-500">points</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Locked Achievements */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Target className="w-5 h-5 text-gray-500 mr-2" />
              Locked ({lockedAchievements.length})
            </h4>
            <div className="space-y-3">
              {lockedAchievements.map((achievement) => (
                <div key={achievement.id} className="flex items-center p-4 bg-gray-100 rounded-xl shadow-lg border-2 border-gray-200 opacity-60">
                  <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center mr-4">
                    <achievement.icon className="w-6 h-6 text-gray-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-600">{achievement.title}</div>
                    <div className="text-sm text-gray-500">{achievement.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-500">+{achievement.points}</div>
                    <div className="text-xs text-gray-400">points</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Progress Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-2xl p-8 border-4 border-blue-200">
        <div className="flex items-center justify-center mb-6">
          <Award className="w-8 h-8 text-blue-600 mr-3" />
          <h3 className="text-2xl font-black text-gray-900">This Week's Progress</h3>
        </div>
        
        <div className="text-center">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-green-600">{completedLessons}</div>
                <div className="text-sm text-gray-600">Lessons Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">{vocabWords}</div>
                <div className="text-sm text-gray-600">Words Learned</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600">{studyStreak}</div>
                <div className="text-sm text-gray-600">Day Streak</div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-lg font-semibold text-gray-700">
                Keep up the great work! 🎉
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GamificationSystem;
